import offlineStorage from './offlineStorage'
import { EventBus } from '@/utils/eventBus'
import fileUploadService from '@/services/fileUploadService'

class AxiosOfflineInterceptor {
  constructor () {
    this.isOnline = navigator.onLine
    this.syncInProgress = false
    this.initEventListeners()
  }

  initEventListeners () {
    window.addEventListener('online', () => {
      this.isOnline = true
      EventBus.$emit('network-status-changed', true)
      this.syncPendingRequests()
      // Also process file upload queue when back online
      fileUploadService.processQueue()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      EventBus.$emit('network-status-changed', false)
    })
  }

  setupInterceptor (axiosInstance) {
    // Request interceptor
    axiosInstance.interceptors.request.use(
      async (config) => {
        // Add unique request ID for tracking
        config.requestId = this.generateRequestId()

        if (!this.isOnline && this.isMutationRequest(config.method)) {
          // Queue the request
          const requestId = await offlineStorage.addRequest({
            baseUrl: config.baseURL,
            url: config.url,
            method: config.method,
            data: config.data,
            headers: config.headers,
            params: config.params,
            requestId: config.requestId
          })

          // Return a mock response for offline mutations
          return Promise.reject(
            new Error(JSON.stringify({
              offline: true,
              queued: true,
              requestId,
              message: 'Request queued for sync',
              config
            }))
          )
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle network errors for mutation requests
        if (
          !navigator.onLine &&
          error.config &&
          this.isMutationRequest(error.config.method)
        ) {
          const requestId = await offlineStorage.addRequest({
            baseUrl: error.config.baseURL,
            url: error.config.url,
            method: error.config.method,
            data: error.config.data,
            headers: error.config.headers,
            params: error.config.params,
            requestId: error.config.requestId
          })

          // Return mock success response
          return Promise.resolve({
            data: {
              queued: true,
              requestId,
              message: 'Request queued for offline sync',
              timestamp: Date.now()
            },
            status: 202,
            statusText: 'Accepted',
            headers: {},
            config: error.config
          })
        }

        return Promise.reject(error)
      }
    )
  }

  isMutationRequest (method) {
    return ['post', 'put', 'delete', 'patch'].includes(method?.toLowerCase())
  }

  generateRequestId () {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  async syncPendingRequests () {
    if (this.syncInProgress) return

    this.syncInProgress = true
    EventBus.$emit('sync-started')

    try {
      const pendingRequests = await offlineStorage.getAllRequests()
      const stats = { success: 0, failed: 0 }

      for (const request of pendingRequests) {
        try {
          const fullUrl = request.baseUrl + '/' + request.url
          // Extract only the actual headers (not Axios config headers)
          const cleanHeaders = {}
          if (request.headers) {
            // Only copy actual HTTP headers, skip Axios internal config
            const validHeaders = ['Authorization', 'Content-Type', 'Accept', 'X-Requested-With']
            validHeaders.forEach(key => {
              if (request.headers[key]) {
                cleanHeaders[key] = request.headers[key]
              }
            })
          }

          const response = await fetch(fullUrl, {
            method: request.method,
            headers: {
              'Content-Type': 'application/json',
              ...cleanHeaders
            },
            body: JSON.stringify(request.data)
          })

          if (response.ok) {
            await offlineStorage.updateRequestStatus(request.id, 'synced')
            stats.success++
            EventBus.$emit('request-synced', request)
          } else {
            await offlineStorage.updateRequestStatus(request.id, 'failed')
            stats.failed++
          }
        } catch (error) {
          await offlineStorage.updateRequestStatus(request.id, 'failed')
          stats.failed++
          console.error('Failed to sync request:', error)
        }
      }

      EventBus.$emit('sync-completed', stats)

      // Clean up synced requests after successful sync
      const syncedRequests = await offlineStorage.getRequestsByStatus('synced')
      for (const req of syncedRequests) {
        await offlineStorage.deleteRequest(req.id)
      }
    } finally {
      this.syncInProgress = false
    }
  }

  // Manual sync trigger
  async forceSyncNow () {
    if (this.isOnline) {
      return await this.syncPendingRequests()
    }
    return Promise.reject(new Error('Cannot sync while offline'))
  }

  // Get sync status
  async getSyncStatus () {
    const stats = await offlineStorage.getStatistics()
    return {
      ...stats,
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress
    }
  }
}

export default new AxiosOfflineInterceptor()
